import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { StatusSidebar } from "@/components/StatusSidebar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, formatTime, formatISTTimestamp } from "@/lib/dateTime";
import { can, canCR, apptStagePage } from "@/lib/permissions";
import { DiscardConfirm } from "@/components/DiscardConfirm";
import { Link } from "react-router-dom";
import { CustomerNameFields } from "@/components/CustomerNameFields";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowLeft,
  Save,
  Pencil,
  MessageCircle,
  User,
  Car,
  Calendar,
  Clock,
  History,
  Edit2,
  CheckCircle,
  Circle,
  XCircle,
  ExternalLink,
  ClipboardList,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const AppointmentDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [appointment, setAppointment] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [cres, setCres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submittingStatus, setSubmittingStatus] = useState(false);
  const submittingStatusRef = useRef(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [branches, setBranches] = useState([]);

  // Update Status popup (today-only day-of Reschedule / Cancel).
  // Matches Upcoming's N-1 popup shape but with these differences:
  //  - Writes appointment_day_outcome (not appointment_status / n_minus_1_confirmation_status)
  //  - Day-of Cancelled sets ONLY appointment_day_outcome; Booking Status stays "Booked"
  //  - Backend reschedule_date guard at appointments.py:347 creates the child appointment
  //    automatically when reschedule_date is present — no extra call from here.
  const [updateStatusPopup, setUpdateStatusPopup] = useState({
    open: false,
    mode: "",           // "Rescheduled" | "Cancelled"
    rescheduleDate: "",
    rescheduleRemarks: "",
    cancelReason: "",
  });

  const today = new Date().toISOString().split("T")[0];
  const tomorrow = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  })();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Matrix permissions — the stage page (today/upcoming/history) decides
  // which Customer Relations page key gates each action. Past records
  // (history stage) are editable only with history.master.
  const stagePage = apptStagePage(appointment?.appointment_date, today);
  const isPastStage = stagePage === "history";
  const canUpdateStage = canCR(user, stagePage, "update");
  const canEditStage = isPastStage ? can(user, "history", "master") : canCR(user, stagePage, "edit");
  const canMasterStage = can(user, stagePage, "master");

  useEffect(() => {
    fetchData();
  }, [id]);

  // Auto-enable edit mode when navigated with ?edit=true
  useEffect(() => {
    if (searchParams.get("edit") === "true" && appointment && canEditStage) {
      setEditMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, appointment]);

  const fetchData = async () => {
    try {
      const [apptRes, logsRes, settingsRes, cresRes, branchesRes] = await Promise.all([
        axios.get(`${API}/appointments/${id}`, { withCredentials: true }),
        axios.get(`${API}/appointments/${id}/activity`, { withCredentials: true }),
        axios.get(`${API}/settings`, { withCredentials: true }),
        axios.get(`${API}/users/cres`, { withCredentials: true }),
        axios.get(`${API}/branches`, { withCredentials: true }),
      ]);

      setAppointment(apptRes.data);
      setFormData(apptRes.data);
      setActivityLogs(logsRes.data);
      setSettings(settingsRes.data);
      setCres(cresRes.data);
      setBranches(branchesRes.data || []);
    } catch (error) {
      toast.error("Failed to load appointment");
      navigate("/day");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      
      // Only include genuinely changed fields (treat null/undefined/"" as equivalent)
      Object.keys(formData).forEach((key) => {
        const newVal = formData[key];
        const oldVal = appointment[key];
        const isEmpty = (v) => v === null || v === undefined || v === "";
        if (isEmpty(newVal) && isEmpty(oldVal)) return;
        if (newVal !== oldVal) {
          updateData[key] = formData[key];
        }
      });

      if (Object.keys(updateData).length === 0) {
        toast.info("No changes to save");
        setEditMode(false);
        setSaving(false);
        return;
      }

      await axios.put(`${API}/appointments/${id}`, updateData, {
        withCredentials: true,
      });

      toast.success("Appointment updated");
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleQuickUpdate = async (field, value) => {
    try {
      await axios.put(`${API}/appointments/${id}`, { [field]: value }, {
        withCredentials: true,
      });
      toast.success("Updated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    }
  };

  // Handler for "Reported" — hands off to Reception pre-filled. The outcome
  // is NOT flipped here. The backend flips appointment_day_outcome to
  // "Reported" only when the Reception entry is actually saved (see
  // create_reception_entry — it updates the linked appointment atomically
  // via linked_appointment_id). If the receptionist closes the wizard
  // without saving, the booking stays in its previous state — no risk of
  // a Reported booking with no matching Reception entry.
  const handleMarkReported = () => {
    navigate("/customer-relations/reception", { state: { fromAppointment: appointment } });
  };

  // Submit the day-of Update Status popup.
  const submitUpdateStatus = async () => {
    if (submittingStatusRef.current) return;
    const { mode, rescheduleDate, rescheduleRemarks, cancelReason } = updateStatusPopup;
    let payload;
    if (mode === "Rescheduled") {
      if (!rescheduleDate) { toast.error("Please select a new date"); return; }
      if (!rescheduleRemarks.trim()) { toast.error("Please enter a reason"); return; }
      // Backend guard at appointments.py:347 auto-creates the child appointment
      // when reschedule_date is present. Do not send appointment_status here —
      // the booking existed and was real; only the day's outcome was a reschedule.
      payload = {
        appointment_day_outcome: "Rescheduled",
        reschedule_date: rescheduleDate,
        reschedule_remarks: rescheduleRemarks,
      };
    } else if (mode === "Cancelled") {
      if (!cancelReason.trim()) { toast.error("Please enter a cancellation reason"); return; }
      // CRITICAL: day-of cancellation sets ONLY appointment_day_outcome.
      // appointment_status stays "Booked" — the booking was real and existed;
      // only the day's outcome was a cancellation. (Cancelling ahead via the
      // Upcoming N-1 popup is what sets appointment_status = "Cancelled".)
      payload = {
        appointment_day_outcome: "Cancelled",
        cancel_reason: cancelReason,
      };
    } else {
      return;
    }
    setSubmittingStatus(true);
    submittingStatusRef.current = true;
    try {
      await axios.put(`${API}/appointments/${id}`, payload, { withCredentials: true });
      toast.success(`Marked as ${mode}`);
      setUpdateStatusPopup({ open: false, mode: "", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSubmittingStatus(false);
      submittingStatusRef.current = false;
    }
  };

  const isAppointmentDay = appointment?.appointment_date === today;

  const assignedCreName =
    cres.find((c) => c.user_id === appointment?.assigned_cre_user)?.name ||
    appointment?.cre_name || "-";

  // Master-only escape hatch for wrongly-created appointments.
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/appointments/${id}`, { withCredentials: true });
      toast.success("Appointment deleted");
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!appointment) return null;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="appointment-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-sm"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Button>
          <div>
            <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tighter uppercase">
              {appointment.customer_name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-500">{appointment.booking_id || `#${appointment.sl_no}`}</span>
              {appointment.priority_customer && (
                <Badge className="bg-black text-white rounded-sm text-xs">PRIORITY</Badge>
              )}
              {appointment.is_rescheduled && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge className="bg-amber-100 text-amber-800 rounded-sm text-xs cursor-help" data-testid="reschedule-badge">[R]</Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px] p-3 bg-gray-100 border-0">
                      {(() => {
                        const entries = (appointment.reschedule_history || []).filter(h => h.from_date);
                        return entries.length > 0 ? (
                          <>
                            <p className="font-bold text-sm mb-2 text-black">{entries.length} reschedule{entries.length !== 1 ? "s" : ""}</p>
                            {entries.map((h, i) => (
                              <p key={i} className="text-sm text-black mb-1">&gt; {formatDate(h.from_date)}</p>
                            ))}
                          </>
                        ) : (
                          <p className="text-sm text-black">Rescheduled</p>
                        );
                      })()}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Update Status — today-only, day-of Reschedule / Cancel. Open to
              everyone: many staff have no edit access; this is their only path
              to record a same-day reschedule or cancellation.
              Disabled once outcome is "Reported" — that is a terminal state
              (vehicle physically received via Reception). Other outcomes
              (Rescheduled / Cancelled / Pending / empty) remain flippable to
              Reported since a rescheduled or cancelled customer may still
              show up. */}
          {appointment.appointment_date === today && canUpdateStage && (
            <DropdownMenu>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-sm"
                        disabled={appointment.appointment_day_outcome === "Reported"}
                        data-testid="update-status-btn"
                      >
                        <ClipboardList className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p className="text-sm">{appointment.appointment_day_outcome === "Reported" ? "Reported — status is final" : "Update Status"}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  data-testid="update-status-reported"
                  onClick={handleMarkReported}
                >
                  Reported
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="update-status-rescheduled"
                  onClick={() => setUpdateStatusPopup({ open: true, mode: "Rescheduled", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" })}
                >
                  Rescheduled
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="update-status-cancelled"
                  onClick={() => setUpdateStatusPopup({ open: true, mode: "Cancelled", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" })}
                >
                  Cancelled
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            variant="outline"
            size="icon"
            className="rounded-sm"
            onClick={() => window.open(`https://wa.me/91${appointment.customer_phone}`)}
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          {canEditStage && (
            <Button
              variant="outline"
              size="icon"
              className="rounded-sm"
              onClick={() => editMode ? handleSave() : setEditMode(true)}
              disabled={saving}
              data-testid="edit-icon-btn"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
              ) : editMode ? (
                <Save className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <Pencil className="w-4 h-4" strokeWidth={1.5} />
              )}
            </Button>
          )}
          {canMasterStage && (
            <Button
              variant="outline"
              size="icon"
              className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-appointment-btn"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Appointment Details */}
          <Card className="border border-gray-200 rounded-sm shadow-none">
            <CardHeader className="border-b border-gray-100 pb-4">
              <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase flex items-center gap-2">
                <Calendar className="w-5 h-5" strokeWidth={1.5} />
                Appointment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <Label className="form-label">Date</Label>
                  {editMode ? (
                    <Input
                      type="date"
                      value={formData.appointment_date}
                      onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                      className="rounded-sm"
                    />
                  ) : (
                    <p className="text-lg">{formatDate(appointment.appointment_date)}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">Time</Label>
                  {editMode ? (
                    <Input
                      type="time"
                      value={formData.appointment_time}
                      onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                      className="rounded-sm"
                    />
                  ) : (
                    <p className="text-lg font-bold">{formatTime(appointment.appointment_time)}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">Branch</Label>
                  {editMode ? (
                    <Select
                      value={formData.branch}
                      onValueChange={(v) => setFormData({ ...formData, branch: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => (
                          <SelectItem key={b.branch_id || b.location} value={b.location}>{b.location}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm">{appointment.branch}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">Source</Label>
                  {editMode ? (
                    <Select
                      value={formData.source}
                      onValueChange={(v) => setFormData({ ...formData, source: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {settings?.sources?.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm mt-1" data-testid="source-value">{appointment.source || "-"}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">Service Type</Label>
                  {editMode ? (
                    <Select
                      value={formData.service_type}
                      onValueChange={(v) => setFormData({ ...formData, service_type: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {settings?.service_types?.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm mt-1" data-testid="service-type-value">{appointment.service_type || "-"}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">Service Advisor</Label>
                  {editMode || (canUpdateStage && isAppointmentDay) ? (
                    <Select
                      value={formData.allocated_sa || ""}
                      onValueChange={(v) => {
                        if (editMode) {
                          setFormData({ ...formData, allocated_sa: v });
                        } else {
                          handleQuickUpdate("allocated_sa", v);
                        }
                      }}
                    >
                      <SelectTrigger className="rounded-sm" data-testid="sa-quick-select">
                        <SelectValue placeholder="Assign SA" />
                      </SelectTrigger>
                      <SelectContent>
                        {settings?.service_advisors?.map((sa) => (
                          <SelectItem key={sa} value={sa}>{sa}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm">{appointment.allocated_sa || "-"}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Info */}
          <Card className="border border-gray-200 rounded-sm shadow-none">
            <CardHeader className="border-b border-gray-100 pb-4">
              <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase flex items-center gap-2">
                <User className="w-5 h-5" strokeWidth={1.5} />
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {editMode && (
                <div className="mb-6">
                  <CustomerNameFields
                    formData={formData}
                    updateField={(f, v) => setFormData((prev) => ({ ...prev, [f]: v }))}
                  />
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6">
                {!editMode && (
                  <div>
                    <Label className="form-label">Name</Label>
                    <p className="font-medium">{appointment.customer_name}</p>
                  </div>
                )}
                <div>
                  <Label className="form-label">Phone</Label>
                  {editMode ? (
                    <Input
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      className="rounded-sm"
                    />
                  ) : (
                    <p className="">{appointment.customer_phone}</p>
                  )}
                </div>
                <div className={editMode ? "md:col-span-2" : "md:col-span-2"}>
                  <Label className="form-label">Email</Label>
                  {editMode ? (
                    <Input
                      type="email"
                      value={formData.customer_email || ""}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      className="rounded-sm"
                    />
                  ) : (
                    <p className="text-sm">{appointment.customer_email || "-"}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vehicle Info */}
          <Card className="border border-gray-200 rounded-sm shadow-none">
            <CardHeader className="border-b border-gray-100 pb-4">
              <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase flex items-center gap-2">
                <Car className="w-5 h-5" strokeWidth={1.5} />
                Vehicle
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="min-w-0">
                  <Label className="form-label">Registration</Label>
                  {editMode ? (
                    <Input
                      value={formData.vehicle_reg_no || ""}
                      onChange={(e) => setFormData({ ...formData, vehicle_reg_no: e.target.value.toUpperCase() })}
                      className="rounded-sm uppercase"
                    />
                  ) : (
                    <p className="font-bold">{appointment.vehicle_reg_no || "-"}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">VIN</Label>
                  {editMode ? (
                    <Input
                      value={formData.vin || ""}
                      onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                      className="rounded-sm uppercase"
                    />
                  ) : appointment.vin && appointment.vehicle_id ? (
                    <Link 
                      to={`/vehicles/${appointment.vehicle_id}`}
                      className="font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                    >
                      {appointment.vin}
                      <ExternalLink className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                    </Link>
                  ) : (
                    <p className="font-bold text-gray-600">{appointment.vin || "-"}</p>
                  )}
                </div>
                <div className="min-w-0">
                  <Label className="form-label">Model</Label>
                  {editMode ? (
                    <Input
                      value={formData.model || ""}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="rounded-sm"
                    />
                  ) : (
                    <p>{appointment.model || "-"}</p>
                  )}
                </div>
                <div className="min-w-0">
                  <Label className="form-label">Current KM</Label>
                  {editMode ? (
                    <Input
                      type="number"
                      value={formData.current_km || ""}
                      onChange={(e) => setFormData({ ...formData, current_km: parseInt(e.target.value) || null })}
                      className="rounded-sm"
                    />
                  ) : (
                    <p className="">{appointment.current_km || "-"}</p>
                  )}
                </div>
              </div>
              
              {appointment.specific_repair_request && (
                <div className="mt-6">
                  <Label className="form-label">Repair Request</Label>
                  {editMode ? (
                    <Textarea
                      value={formData.specific_repair_request || ""}
                      onChange={(e) => setFormData({ ...formData, specific_repair_request: e.target.value })}
                      className="rounded-sm"
                    />
                  ) : (
                    <p className="text-sm mt-2 p-3 bg-gray-50 border border-gray-100 rounded-sm">
                      {appointment.specific_repair_request}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <StatusSidebar
            appointment={appointment}
            editMode={editMode}
            canEdit={canEditStage}
            canUpdate={canUpdateStage}
            canMaster={canMasterStage}
            formData={formData}
            setFormData={setFormData}
            settings={settings}
            today={today}
            tomorrow={tomorrow}
          />

          {/* Assigned CRE — plain text; becomes a dropdown only in edit mode
              (same edit-level gating as the other edit-mode fields) */}
          <Card className="border border-gray-200 rounded-sm shadow-none">
            <CardContent className="p-6">
              <Label className="form-label">Assigned CRE</Label>
              {editMode && canEditStage ? (
                <Select
                  value={formData.assigned_cre_user || appointment.assigned_cre_user}
                  onValueChange={(v) => setFormData({ ...formData, assigned_cre_user: v })}
                >
                  <SelectTrigger className="rounded-sm mt-1" data-testid="cre-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cres.map((c) => (
                      <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm mt-1" data-testid="cre-name-text">{assignedCreName}</p>
              )}
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card className="border border-gray-200 rounded-sm shadow-none">
            <CardHeader className="border-b border-gray-100 pb-4">
              <CardTitle className="font-heading font-bold text-lg tracking-tight uppercase flex items-center gap-2">
                <History className="w-5 h-5" strokeWidth={1.5} />
                Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {activityLogs.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">
                  No activity yet
                </div>
              ) : (
                <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                  {activityLogs.map((log) => (
                    <div key={log.log_id} className="p-4" data-testid={`log-${log.log_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{log.user_name}</p>
                          <p className="text-xs text-gray-500">{log.action}</p>
                          {log.field_changed && (
                            <p className="text-xs text-gray-400 mt-1">
                              {log.old_value} → {log.new_value}
                            </p>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400 whitespace-nowrap">
                          {formatISTTimestamp(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Update Status — Rescheduled dialog (day-of) */}
      <Dialog
        open={updateStatusPopup.open && updateStatusPopup.mode === "Rescheduled"}
        onOpenChange={(open) => !open && setUpdateStatusPopup({ open: false, mode: "", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" })}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="update-status-rescheduled-dialog"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
          onCloseClick={(e) => {
            if (updateStatusPopup.rescheduleDate || updateStatusPopup.rescheduleRemarks) {
              e.preventDefault();
              setDiscardConfirmOpen(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-tight">Rescheduled</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="form-label">New Date</Label>
              <Input
                type="date"
                value={updateStatusPopup.rescheduleDate}
                onChange={(e) => setUpdateStatusPopup({ ...updateStatusPopup, rescheduleDate: e.target.value })}
                className="rounded-sm mt-1"
                data-testid="update-status-reschedule-date"
              />
            </div>
            <div>
              <Label className="form-label">Reason</Label>
              <Textarea
                value={updateStatusPopup.rescheduleRemarks}
                onChange={(e) => setUpdateStatusPopup({ ...updateStatusPopup, rescheduleRemarks: e.target.value })}
                className="rounded-sm mt-1"
                rows={3}
                data-testid="update-status-reschedule-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setUpdateStatusPopup({ open: false, mode: "", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" })}
              data-testid="update-status-reschedule-cancel"
            >Cancel</Button>
            <Button
              size="sm"
              className="rounded-sm bg-black text-white hover:bg-gray-800"
              onClick={submitUpdateStatus}
              disabled={submittingStatus}
              data-testid="update-status-reschedule-submit"
            >Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status — Cancelled dialog (day-of).
          Writes ONLY appointment_day_outcome — booking status stays "Booked". */}
      <Dialog
        open={updateStatusPopup.open && updateStatusPopup.mode === "Cancelled"}
        onOpenChange={(open) => !open && setUpdateStatusPopup({ open: false, mode: "", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" })}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="update-status-cancelled-dialog"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
          onCloseClick={(e) => {
            if (updateStatusPopup.cancelReason) {
              e.preventDefault();
              setDiscardConfirmOpen(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-tight">Cancelled</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="form-label">Reason</Label>
              <Textarea
                value={updateStatusPopup.cancelReason}
                onChange={(e) => setUpdateStatusPopup({ ...updateStatusPopup, cancelReason: e.target.value })}
                className="rounded-sm mt-1"
                rows={3}
                data-testid="update-status-cancel-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setUpdateStatusPopup({ open: false, mode: "", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" })}
              data-testid="update-status-cancel-cancel"
            >Cancel</Button>
            <Button
              size="sm"
              className="rounded-sm bg-black text-white hover:bg-gray-800"
              onClick={submitUpdateStatus}
              disabled={submittingStatus}
              data-testid="update-status-cancel-submit"
            >Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DiscardConfirm
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        onDiscard={() => {
          setDiscardConfirmOpen(false);
          setUpdateStatusPopup({ open: false, mode: "", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" });
        }}
      />

      {/* Delete confirmation — Master-only */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-appointment-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-tight">Delete Appointment</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            This permanently removes the booking for {appointment.customer_name}.
            Use "Cancelled" for normal cancellations — delete only wrongly-created appointments.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="delete-appointment-cancel"
            >Cancel</Button>
            <Button
              size="sm"
              className="rounded-sm bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-appointment-confirm"
            >{deleting ? "Deleting..." : "Delete"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentDetail;
